import React, { FC, useEffect, useState } from "react";
import {
  DataGridPro,
  GridColDef,
  GridDensity,
  GridRowParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { TableProps } from "../types";
import { Box, TablePagination } from "@mui/material";
import { apiClient } from "api";
import TableMenu from "./components/TableMenu";
import { useDispatch, useSelector } from "react-redux";
import { QueryObserverResult } from "react-query";
import { AppState } from "store/types";
import { addCurrentColumns } from "store/table/table.slice";
import { MUI_TABLE_STYLES, TABLE_PAGINATION_STYLES } from "constants/constants";
import { SalesReportsProps } from "api/types";
import ExportIcon from "assets/Icons/replenishment/ExportIcon.svg";
import Alerts from "components/Alerts";
import { EXCEL_MEDIA_TYPE } from "./constants";
import { TitleCase } from "utils/utils";
import { CatalogTrackerSourceOfTruth } from "pages/CatalogTracker/CatalogTrackerHeader";
import { SellerSelectModal } from "./SelectSellerModal/sellerSelectModal";
import UploadModal from "./components/UploadModal";
import { changeUploadSellerSelected } from "store/catalogTracker/catalogTracker.slice";
import {
  catalogTrackerStyle,
  catalogTrackerTableStyle,
  forecastingEditTableDownload,
  forecastingEditTableDownloadingProgress,
} from "./style";

const columns: GridColDef[] = [
  {
    field: "seller_name",
    headerName: "Seller",
    minWidth: 10,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
  },
  {
    field: "asin",
    align: "left",
    headerName: "Asin",
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
  },
  {
    field: "forecast",
    align: "right",
    headerName: "Forecast",
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
  },
  {
    field: "mismatch",
    align: "right",
    headerName: "# of Mismatches",
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
  },
  {
    field: "price",
    align: "center",
    headerName: TitleCase("price"),
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "title",
    align: "center",
    headerName: "Title",
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "brand",
    align: "center",
    headerName: "Brand",
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "description",
    align: "center",
    headerName: TitleCase("description"),
    width: 100,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "bullet_point_1",
    align: "center",
    headerName: TitleCase("bullet_point_1"),
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
    sortable: false,
  },

  {
    field: "bullet_point_2",
    align: "center",
    headerName: TitleCase("bullet_point_2"),
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "bullet_point_3",
    align: "center",
    headerName: TitleCase("bullet_point_3"),
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "bullet_point_4",
    align: "center",
    headerName: TitleCase("bullet_point_4"),
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
    sortable: false,
  },

  {
    field: "bullet_point_5",
    align: "center",
    headerName: TitleCase("bullet_point_5"),
    width: 150,
    disableColumnMenu: true,
    filterable: false,
    headerAlign: "center",
    sortable: false,
  },
];

interface CatalogTrackerTableProps extends TableProps {
  source: string;
  setValue: React.Dispatch<React.SetStateAction<number>>;
  setAttributesAsin: React.Dispatch<React.SetStateAction<string>>;
  setSeller: React.Dispatch<React.SetStateAction<string>>;
  attributeRefetch: QueryObserverResult["refetch"];
}

const CatalogTrackerTable: FC<CatalogTrackerTableProps> = ({
  rows,
  isCheckbox,
  fetching,
  getColumnToSort,
  sortType,
  getPageNumber,
  getPageSize,
  newPageNumber,
  newPageSize,
  source,
  setValue,
  setAttributesAsin,
  setSeller,
  attributeRefetch,
}) => {
  const dispatch = useDispatch();
  const density = useSelector(
    (state: AppState) => state.table.density
  ) as GridDensity;
  const totalRecords = useSelector(
    (state: AppState) => state.catalogTracker.records
  );
  const selectedSellers = useSelector(
    (state: AppState) => state.auth.selectedSellers
  );

  const filters = useSelector((state: AppState) => state.table.columnFilters);
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(1);

  const apiRef = useGridApiRef();
  const [initialRows, setInitialRows] = useState<SalesReportsProps[]>([]);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [clickDownload, setClickDownload] = useState<boolean>(false);
  const [records, setRecords] = useState<number>(0);
  const uploadSellerSelected = useSelector(
    (state: AppState) => state.catalogTracker.uploadSellerSelected
  );

  const [showSellersDropdown, setShowSellersDropdown] = useState(false);

  const handleDropdown = () => {
    setShowSellersDropdown(!showSellersDropdown);
    return !showSellersDropdown;
  };

  const handleCloseUpload = () => {
    dispatch(changeUploadSellerSelected(""));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
    if (getPageNumber) getPageNumber(newPage + 1);
  };

  const handleRowDoubleClick = ({ row }: GridRowParams) => {
    const { asin, seller_name } = row || {};

    const matchedSeller = selectedSellers.find(
      (seller) => seller.name === seller_name
    );
    const matchedSellerId = matchedSeller ? matchedSeller.id : "";

    setValue(1);
    setAttributesAsin(asin || "");
    setSeller(matchedSellerId);
    setTimeout(() => {
      attributeRefetch();
    }, 300);
  };
  useEffect(() => {
    if (totalRecords) {
      setRecords(totalRecords);
    }
  }, [totalRecords]);

  useEffect(() => {
    if (columns.length) {
      dispatch(addCurrentColumns(columns));
    }
  }, [columns]);

  useEffect(() => {
    if (
      typeof newPageNumber !== "undefined" &&
      typeof newPageSize !== "undefined"
    ) {
      setPage(newPageNumber);
      setPageSize(newPageSize);
    }
  }, [newPageNumber, newPageSize]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    if (getPageSize) getPageSize(parseInt(event.target.value, 10));
  };

  useEffect(() => {
    if (rows && rows.length) {
      setInitialRows(rows);
    }
  }, [rows]);

  useEffect(() => {
    if (filters?.length && filters[1].length) {
      setPage(1);
    }
  }, [filters]);

  const CustomFooter = () => {
    return (
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        component="div"
        sx={TABLE_PAGINATION_STYLES}
        count={records}
        showFirstButton
        showLastButton
        rowsPerPage={pageSize}
        page={page - 1}
        labelDisplayedRows={({ from, to, count, page }) => {
          return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
        }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    );
  };

  return (
    <Box sx={catalogTrackerStyle}>
      {showSellersDropdown && (
        <SellerSelectModal
          handleClose={handleDropdown}
          isOpen={showSellersDropdown}
        />
      )}
      <UploadModal
        handleClose={handleCloseUpload}
        open={!!(!!uploadSellerSelected && !showSellersDropdown)}
        widgetName={"mismatchSummary"}
      />
      <Box sx={catalogTrackerTableStyle}>
        <TableMenu
          disableUpload={!!(source !== CatalogTrackerSourceOfTruth.USER_DATA)}
          disableExpand
          widgetName="mismatchSummary"
          callback={handleDropdown}
        />

        <DataGridPro
          components={{
            Footer: CustomFooter,
          }}
          apiRef={apiRef}
          rows={rows}
          sx={MUI_TABLE_STYLES}
          columns={columns}
          pageSize={pageSize}
          headerHeight={52}
          loading={fetching}
          density={density}
          checkboxSelection={isCheckbox ? true : false}
          pagination
          sortingMode="server"
          sortingOrder={["asc", "desc"]}
          onSortModelChange={(newSortModel: GridSortModel) => {
            if (newSortModel && newSortModel.length) {
              const { field, sort } = newSortModel[0];
              if (field && sort) {
                if (getColumnToSort) {
                  if (sortType === "asc") {
                    getColumnToSort(field, "desc");
                  } else {
                    getColumnToSort(field, sort);
                  }
                }
              }
            }
          }}
          onRowDoubleClick={handleRowDoubleClick}
        />
      </Box>

      {clickDownload ? (
        <Box sx={forecastingEditTableDownloadingProgress}>
          <Alerts
            title={"Download in progress"}
            completeTitle={"Download complete"}
            completeSubTitle={"Restock Summary table has been exported."}
            loading={downloading}
            close={() => setClickDownload(false)}
          />
        </Box>
      ) : (
        <Box
          sx={{ ...forecastingEditTableDownload, bottom: 30, right: 50 }}
          onClick={() => {
            setDownloading(true);
            setClickDownload(true);
            apiClient
              .getMismatchSummaryReport(filters)
              .then((res: any) => {
                const link = document.createElement("a");
                link.href = EXCEL_MEDIA_TYPE + res;
                link.setAttribute("download", "CatalogTracker.xlsx");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              })
              .catch((err: Error) => console.log(err))
              .finally(() => {
                setDownloading(false);
                setTimeout(() => {
                  setClickDownload(false);
                }, 3000);
              });
          }}
        >
          <img src={ExportIcon} alt="export" />
        </Box>
      )}
    </Box>
  );
};

export default CatalogTrackerTable;
